import actions from "~/shared/actions";
import type { Country } from "~/shared/reducers/countries";
import type { Action } from "~/utils/actions";

export type SamtykkeStatus = "AUTHORIZED" | "NOT_AUTHORIZED" | "UNANSWERED";

export interface Address {
	zipName: string;
	zipCode: string;
	streetAddress: string;
	addressLine1: string;
	addressLine2: string;
	countryCode: string;
	validForeignAddress: boolean;
}

export interface Customer {
	ssn: string;
	firstName: string;
	lastName: string;
	age: number;
	dateOfBirth: number;
	email: string;
	phoneNumber: string;
	dybdeSamtykke: SamtykkeStatus;
	markedsforingSamtykke: SamtykkeStatus;
	address: Address;
	mobileBank: boolean;
	nettBank: boolean;
	bankSession: boolean;
	bankCustomer: boolean;
	localBankPhone: string;
	localBankEmail: string;
	localBankName: string;
	bankDomain: string;
	bankFid: string;
	authenticated: boolean;
	loMember?: boolean;
	foreignAddress: boolean;
	authorizedForSensitiveInformation: boolean;
	digitaltForstevalgSamtykke: boolean;
	norskPensjonSamtykke: SamtykkeStatus;
	localBankOrgnr: string;
	phoneCountryCode: string;
	paminnelseRisikokartleggingStatus: SamtykkeStatus;
	reconfirmSamtykke: boolean;
	dNumber: boolean;
	isLoWithIndeks?: boolean;
	distributor?: Distributor;
	trusselutsatt?: boolean;
}

export interface Distributor {
	bankOrgNr?: string;
	bankName?: string;
	bankDomain?: string;
	bankNo?: string;
	bankTel?: string;
	bankEmail?: string;
}

export interface FatcaCountry extends Country {
	tin: string;
	address: string;
}

export interface Fatca {
	american: boolean;
	americanSsn: string;
	foreignTax: boolean;
	taxCountries: FatcaCountry[];
}

export interface CustomerF2100AccountNumber {
	accountNumber: string;
	ownsAccount: boolean;
}

interface Meta {
	meta: {
		updatingDybdeSamtykkeStatusFailed: boolean;
		updatingMarkedsforingSamtykkeStatusFailed: boolean;
	};
}

export type CustomerState = Customer & { fatca: Fatca } & { f2100Account: CustomerF2100AccountNumber } & Meta;

export const defaultState: Partial<CustomerState> = {
	meta: {
		updatingDybdeSamtykkeStatusFailed: false,
		updatingMarkedsforingSamtykkeStatusFailed: false,
	},
};

export function getInitialState(defaults: Customer): CustomerState {
	return {
		...defaults,
		...defaultState,
	} as CustomerState;
}

function receiveCustomer(state: CustomerState, { data }: Action): CustomerState {
	return { ...state, ...data };
}

function updatingSamtykke(state: CustomerState, action: Action): CustomerState {
	const { dybdeSamtykkeStatus, markedsforingSamtykkeStatus } = action.data;

	const samtykker = {
		dybdeSamtykke: dybdeSamtykkeStatus || state.dybdeSamtykke,
		markedsforingSamtykke: markedsforingSamtykkeStatus || state.markedsforingSamtykke,
	};

	return { ...state, ...samtykker };
}

function updatedPhoneNumber(state: CustomerState, { data }: Action): CustomerState {
	return { ...state, ...data };
}

function updatedEmail(state: CustomerState, { data }: Action): CustomerState {
	return { ...state, email: data };
}

function receiveFatcaData(state: CustomerState, action: Action): CustomerState {
	if (!action.data) {
		return state;
	}

	return {
		...state,
		fatca: { ...action.data },
	};
}

function setCustomerF2100AccountNumberResponse(state: CustomerState, { data }: Action): CustomerState {
	if (!data.success) {
		return state;
	}
	return {
		...state,
		f2100Account: {
			accountNumber: data.accountNumber,
			ownsAccount: true,
		},
	};
}

export default function customerReducer(state = defaultState as CustomerState, action: Action): CustomerState {
	switch (action.type) {
		case actions.CUSTOMER_RESPONSE:
			return receiveCustomer(state, action);
		case actions.SAMTYKKE_UPDATE_REQUEST:
			return updatingSamtykke(state, action);
		case actions.PHONE_NUMBER_UPDATE_RESPONSE:
			return updatedPhoneNumber(state, action);
		case actions.EMAIL_UPDATE_RESPONSE:
			return updatedEmail(state, action);
		case actions.FATCA_DATA_UPDATE_RESPONSE:
		case actions.FATCA_DATA_RESPONSE:
			return receiveFatcaData(state, action);
		case actions.UPDATE_MARKEDSFORING_SAMTYKKE_RESPONSE:
			return { ...state, markedsforingSamtykke: action.data ? "AUTHORIZED" : "NOT_AUTHORIZED" };
		case actions.UPDATE_DYBDE_SAMTYKKE_RESPONSE:
			return { ...state, dybdeSamtykke: action.data ? "AUTHORIZED" : "NOT_AUTHORIZED" };
		case actions.UPDATE_NORSK_PENSJON_SAMTYKKE_RESPONSE:
			return { ...state, norskPensjonSamtykke: action.data ? "AUTHORIZED" : "NOT_AUTHORIZED" };
		case actions.UPDATE_DIGITALT_FORSTEVALG_SAMTYKKE_RESPONSE:
			return { ...state, digitaltForstevalgSamtykke: action.data };
		case actions.BEKREFT_KONTAKT_INFO_RESPONSE:
			return {
				...state,
				digitaltForstevalgSamtykke: true,
				email: action.data.email || state.email,
				phoneNumber: action.data.phoneNumber || state.phoneNumber,
				phoneCountryCode: action.data.phoneCountryCode || state.phoneCountryCode,
				reconfirmSamtykke: false,
			};
		case actions.CUSTOMER_TIMEOUT:
			return { ...state, authenticated: false };
		case actions.CUSTOMER_F2100_ACCOUNT_NUMBER_RESPONSE:
			return { ...state, f2100Account: action.data };
		case actions.SET_CUSTOMER_F2100_ACCOUNT_NUMBER_RESPONSE:
			return setCustomerF2100AccountNumberResponse(state, action);
		case actions.RESET_POLICIES:
			return { ...state, reconfirmSamtykke: false };
		case actions.FETCH_LO_FORVALTNINGSFORM_RESPONSE:
			return { ...state, isLoWithIndeks: action.data };
		case actions.UPDATE_RISK_ASSESSMENT_REMINDER_RESPONSE:
			return { ...state, paminnelseRisikokartleggingStatus: action.data };
		case actions.IS_LO_MEMBER_RESPONSE:
			return { ...state, loMember: action.data };
		case actions.GET_DISTRIBUTOR_RESPONSE:
			return { ...state, distributor: action.data };
		default:
			return state;
	}
}
